import { useEffect, useState } from 'react';
import { 
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { COLORS } from '../../styles/colors';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Modal from '../Modal';
import { Add } from '@mui/icons-material';
import UserService from '../../services/userService';
import AuthService from '../../services/authService';
import MedicationService from '../../services/medicationService';
import { debounce } from 'lodash';
import { PhotonMedication } from '../MedicationsForm/helper';
import _ from 'lodash';

interface AllergiesFormProps {
  allergies?: string[];
  apptId?: string;
  updateHistory?: (history: any) => void;
}

const AllergiesForm = (props: AllergiesFormProps) => {
  const { user } = AuthService.getAuth();
  const { allergies, apptId, updateHistory } = props;
  
  // State management
  const [isAddingAllergy, setIsAddingAllergy] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [savedAllergies, setSavedAllergies] = useState<string[]>(allergies || []);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [medicationOptions, setMedicationOptions] = useState<PhotonMedication[]>([]);
  
  // Form state
  const [editingAllergy, setEditingAllergy] = useState<string | null>(null);
  const [selectedAllergy, setSelectedAllergy] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  // Search medications with debounce
  const searchMedications = debounce(async (searchQuery: string) => {
    setQuery(searchQuery);

    if (!searchQuery) {
      setMedicationOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await MedicationService.searchMedications(searchQuery);
      setMedicationOptions(response.data);
    } catch (error) {
      console.error('Error searching medications:', error);
    } finally {
      setLoading(false);
    }
  }, 300);

  const updateAllergies = async (newSavedAllergies: string[]) => {
    if (!user) return null;

    try {
      const response = await UserService.updateMedicalHistory(
        user.id,
        { form: { medications: { medicationAllergies: newSavedAllergies } } },
      );

      if (apptId) {
        await UserService.updateAppointmentProgress(apptId, { followUpProgress: 'medications', medicalId: response.data.id });
      }

    } catch (error) {
      console.error('Error updating paperwork:', error);
    }

    setSavedAllergies(newSavedAllergies);
    handleCancel();
  };

  const isFormValid = () => !!editingAllergy;

  const handleAdd = async () => {
    if (!isFormValid() || !editingAllergy) return;

    const newSavedAllergies = [...savedAllergies, editingAllergy];
    await updateAllergies(newSavedAllergies);
  };

  const handleEdit = (allergy: string) => {
    setSelectedAllergy(allergy);
    setEditingAllergy(allergy);
    setIsEditMode(true);
    setIsAddingAllergy(true);
  };

  const handleDelete = async () => {
    if (!selectedAllergy) return;

    const newSavedAllergies = savedAllergies.filter((a) => a !== selectedAllergy);
    await updateAllergies(newSavedAllergies);
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !selectedAllergy || !editingAllergy) return;

    const newSavedAllergies = savedAllergies.map((a) => a === selectedAllergy ? editingAllergy : a);
    await updateAllergies(newSavedAllergies);
  };

  const handleCancel = () => {
    setSelectedAllergy(null);
    setEditingAllergy(null);
    setIsAddingAllergy(false);
    setIsEditMode(false);
    setDeleteModalOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
      {/* Header */}
      {!isAddingAllergy && (
        <Box sx={{ mb: 1, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Medication Allergies
          </Typography>
          <Typography variant="body1">
            If you have any allergies to medications, please add them below.
          </Typography>
        </Box>
      )}

      {/* Allergies List Panel */}
      {!isAddingAllergy && (
        <Card sx={{ mt: 2, p: 1 }}>
          <CardContent sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'center' }}>
            {savedAllergies.length > 0 ? (
              <Box sx={{ mb: 1, gap: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Button
                  variant="contained"
                  onClick={() => setIsAddingAllergy(true)}
                  className="green-action-btn"
                  sx={{ alignSelf: 'flex-end', display: 'flex', gap: 0.5 }}
                >
                  <Add sx={{ color: 'primary.contrastText' }} />
                  Add Allergy
                </Button>
                {savedAllergies.map((allergy, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      py: 1, 
                      px: 2, 
                      bgcolor: 'grey.200', 
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {allergy}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                      <IconButton 
                        onClick={() => handleEdit(allergy)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => {
                          setSelectedAllergy(allergy);
                          setDeleteModalOpen(true);
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No allergies added yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setIsAddingAllergy(true)}
                  size="large"
                >
                  Add Allergy
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Allergy Form Panel */}
      {isAddingAllergy && (
        <Card sx={{ mt: 2, p: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h5" fontWeight="bold" textAlign="center">
                {isEditMode ? 'Edit Medication Allergy' : 'Add New Medication Allergy'}
              </Typography>

              <Autocomplete
                freeSolo
                options={[
                  ...(query ? [query] : []),
                  ..._.uniq(medicationOptions.map(med => med.brandName || med.genericName))
                ].filter((option): option is string => !!option)}
                value={editingAllergy}
                onChange={(_, value) => setEditingAllergy(value)}
                onInputChange={(_, value) => {
                  searchMedications(value);
                  setEditingAllergy(value);
                }}
                loading={loading}
                noOptionsText={!query.length ? "Type to search for medications" : "No options"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Allergy"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={isEditMode ? handleUpdate : handleAdd}
                  disabled={!isFormValid()}
                  sx={{ 
                    width: 'fit-content',
                    px: 4
                  }}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ 
                    width: 'fit-content',
                    px: 4
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        children={
          <Box>
            <DialogTitle>
              <Typography variant="h5" fontWeight="bold">
                Delete Allergy
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Are you sure you want to delete {selectedAllergy}? This action cannot be undone.
              </Typography>
            </DialogContent>
          </Box>
        }
        buttonActions={[{
          text: 'Cancel',
          style: 'cancel',
          action: () => setDeleteModalOpen(false)
        }, {
          text: 'Delete',
          style: 'confirm',
          action: handleDelete
        }]}
      />
    </Box>
  );
};

export default AllergiesForm;
